import { canUseDOM, injectScript } from '@/utils/dom-utils';
import { getCookie } from '@/utils/cookies-util';
import { getType, isFn, isNoE, isNoW } from '@/utils/obj-utils';
import {
  concatString,
  eq,
  formatString,
  ifEmpty,
  ifEmptyField,
  randomString,
  toCapitalize,
} from '@/utils/string-utils';
import lzString from 'lz-string';
import { merge, isString, isArray } from 'lodash-es';
import * as querystring from 'querystring';
import { getLangFromUrl } from '@/utils/uri-utils';
import utils from '@/utils/index';
import { getRatioVal } from '@/utils/gs-utils';
export const headerHeights = [52, 52, 64];
/**@param {ComponentOverallSetting} props*/
export const overallSettingClasses = (props) => {
  const paddingTop = settingValue(props.fields.paddingTop);
  const paddingBottom = settingValue(props.fields.paddingBottom);
  const marginTop = settingValue(props.fields.marginTop);
  const marginBottom = settingValue(props.fields.marginBottom);
  const backColor = String(props?.fields?.backColor?.displayName).toLowerCase();
  const foreColorRaw = String(props?.fields?.foreColor?.displayName).toLowerCase();
  return [
    {
      [`overall-pd-top-${paddingTop}`]: paddingTop,
      [`overall-pd-bottom-${paddingBottom}`]: paddingBottom,
      [`overall-mg-top-${marginTop}`]: marginTop,
      [`overall-mg-bottom-${marginBottom}`]: marginBottom,
      [`theme-${backColor}`]: backColor,
      [`overall-fore-${foreColorRaw}`]: foreColorRaw,
    },
  ];
};
export const themeClass = (props, defaultTheme) => {
  const themeVal = settingValue(props?.fields?.theme, defaultTheme);
  return {
    [`theme-${themeVal}`]: themeVal,
  };
};
export const settingValue = (item, defaultValue) => {
  let val = item?.fields?.phrase?.value;
  if (isNoE(val)) {
    val = defaultValue;
  }
  return val;
};
export const settingVal = (item, defaultItem) => {
  return settingValue(item, defaultItem?.fields?.phrase?.value);
};
export const gridLayoutClass = (item, defaultValue) => {
  const val = settingValue(item, defaultValue);
  return val ? `grid-layout-${val}` : null;
};
export const gridWidth = (span, includeGap = true, columnCount = 24, gap = 8) => {
  if (!canUseDOM()) return 0;
  const perColumnWidth = (window.innerWidth - gap * (columnCount - 1)) / columnCount;
  let result = perColumnWidth * span + (span - 1) * gap;
  if (includeGap) {
    result += gap;
  }
  return Math.floor(result);
};
export const gridWidthMobile = (span, includeGap = true) => {
  return gridWidth(span, includeGap, 12);
};
export const gridPercent = (span) => {
  return Math.floor((1000 * span) / 24) / 10 + 'vw';
};

export const componentId = (rendering) => `com-${rendering.uid}`;

export const classNames = (className, fn) => [className, ...fn];

export const getComponentProps = (props, className) => {
  const { rendering, fields } = props;
  return {
    id: componentId(rendering),
    [`data-source-id`]: rendering.dataSource,
    className: classNames(className, overallSettingClasses(fields)),
  };
};

export const renderBullet = (index, className, total) => {
  if (!total) total = 1;
  const radius = 15;
  const size = 34;
  const center = size / 2;
  const anglePerItem = 360 / total;
  const startDegree = anglePerItem * index;
  const endDegree = anglePerItem * (index + 1);
  const startAngle = (startDegree * Math.PI) / 180;
  const endAngle = (endDegree * Math.PI) / 180;
  const startX = center + radius * Math.sin(startAngle);
  const startY = center - radius * Math.cos(startAngle);
  const endX = center + radius * Math.sin(endAngle);
  const endY = center - radius * Math.cos(endAngle);
  return `<div class="${className}"><svg viewBox="0 0 ${size} ${size}"><circle cx="${center}" cy="${center}" r="${radius}" /><path d="M ${startX} ${startY} A ${radius},${radius} 0 0 1 ${endX},${endY}" /></svg></div>`;
};

export const loadGMap = (page) => {
  return new Promise((resolve) => {
    if (!canUseDOM()) {
      resolve(null);
      return;
    }
    if (window.google?.maps) {
      resolve(window.google.maps);
      return;
    }
    const [gmapKey] = getGlobalConfigs(page, 'gmapKey');
    if (!gmapKey?.value) {
      resolve(null);
      return;
    }
    const method = `onGMapReady${Math.ceil(Math.random() * 100000)}${Date.now()}`;
    window[method] = () => {
      resolve(window.google.maps);
    };
    injectScript(
      `https://maps.google.com/maps/api/js?key=${gmapKey?.value}&libraries=geometry,places&callback=${method}&language=${page.itemLanguage}`
    )
      .then((loaded) => {
        if (!loaded) {
          resolve(null);
        }
      })
      .catch(() => {
        resolve(null);
      });
  });
};

export const loadAMap = (page) => {
  const [amapKey] = getGlobalConfigs(page, 'amapKey');
  return new Promise((resolve) => {
    if (!canUseDOM()) {
      resolve(null);
      return;
    }
    if (window.AMap) {
      resolve(window.AMap);
      return;
    }
    const method = `onAMapReady${Math.ceil(Math.random() * 100000)}${Date.now()}`;
    window[method] = () => {
      resolve(window.AMap);
    };
    injectScript(`https://webapi.amap.com/maps?v=2.0&key=${amapKey?.value}&callback=${method}`)
      .then((loaded) => {
        if (!loaded) {
          resolve(null);
        }
      })
      .catch(() => {
        resolve(null);
      });
  });
};
export const getGlobalConfig = (page, key, defaultValue = null) => {
  const { globalConfig } = page?.fields ?? {};
  const globalConfigFields = globalConfig?.fields ?? {};
  return globalConfigFields[key] ?? defaultValue;
};
export const getGlobalConfigs = (page, ...keys) => {
  let result = [];
  for (let key of keys) {
    result.push(getGlobalConfig(page, key));
  }
  return result;
};

/**
 * 获取 publishableKey 配置
 * @param {Object} page - 页面对象
 * @param {*} defaultValue - 默认值
 * @returns {*} publishableKey 配置值
 */
export const getPublishableKey = (page, defaultValue = null) => {
  return getGlobalConfig(page, 'publishableKey', defaultValue);
};
export const getGlobalCfgValues = (page, keys, defaults) => {
  const items = getGlobalConfigs(page, keys);
  const result = [];
  for (let i = 0, len = items.length; i < len; i++) {
    const item = items[i];
    const defaultVal = defaults?.length > i ? defaults[i] : null;
    result.push(ifEmpty(item?.value, defaultVal));
  }
  return result;
};
export const handleComClass = (className) => {
  if (!className) return [];
  const classType = getType(className);
  if (classType === '[object Array]') {
    return className;
  } else if (classType === '[object Object]') {
    return [className];
  } else if (classType === '[object String]') {
    return [className];
  }
  return [];
};
export const buildAddress = (...addresses) => {
  return concatString(addresses, ', ', (str) => {
    str = str.replace(/,\s*?,/gi, ',');
    str = str.replace(/,\s*?$/gi, '');
    str = str.replace(/^\s*?,/gi, '');
    return str;
  });
};
export const isLocalServer = () => {
  if (!canUseDOM()) return false;
  return /https?:\/\/localhost/gi.test(window.location.origin);
};
let oneTrustLoaded = false;
export const loadOneTrust = async (page) => {
  try {
    if (!oneTrustLoaded) {
      const [oneTrustScript, oneTrustDomainId] = getGlobalConfigs(
        page,
        'oneTrustScript',
        'oneTrustDomainId'
      );
      if (!oneTrustScript?.value || !oneTrustDomainId?.value) return false;
      const head = document.querySelector('head');
      const sc1 = document.createElement('script');
      sc1.type = 'text/javascript';
      sc1.innerHTML = `function OptanonWrapper(){
        var container = document.getElementById('onetrust-banner-sdk');
        if (container) {
          var header = container.querySelector('.banner-header');
          var acceptBtn = document.getElementById("onetrust-accept-btn-handler");
          var declineBtn = document.getElementById("onetrust-reject-all-handler");
          var cookieSettingsBtn = document.getElementById("onetrust-pc-btn-handler");
          var btnContainer = document.getElementById("onetrust-button-group");
          var oldContainer = btnContainer?.querySelector('.banner-actions-container');
          header?.remove();
          oldContainer?.remove();
          btnContainer?.classList.add('onetrust-buttons');
          btnContainer?.append(acceptBtn, declineBtn, cookieSettingsBtn);
        }
      }`;
      head.appendChild(sc1);
      const sc = document.createElement('script');
      sc.type = 'text/javascript';
      sc.src = oneTrustScript?.value;
      sc.async = true;
      sc.setAttribute('data-document-language', true);
      sc.setAttribute('data-domain-script', oneTrustDomainId.value);
      head.appendChild(sc);
      oneTrustLoaded = true;
      return true;
    }
  } catch (ex) {
    console.log('one trust ex', ex);
    return false;
  }
};
export const getRegionName = (page, _domain) => {
  /**@type Array<SiteArea>*/
  const [c] = getGlobalConfigs(page, 'country');
  const [siteIdentification] = getGlobalConfigs(page, 'siteIdentification');
  const pageAlpha2Code = getPageAlpha2Code(page);
  if (!c) return;
  const country = c?.fields?.country;
  for (let a of country) {
    const isLang = a.fields?.languages.some((lang) =>
      eq(lang?.fields?.language?.value, pageAlpha2Code)
    );
    // console.log('domain',isLang,domain,a.fields?.tag?.value,domain.indexOf(a.fields?.tag?.value) != -1)
    console.log('siteIdentification', siteIdentification?.fields?.identifying?.value);
    // if (a.fields.languages.some((lang) => eq(getAlpha2CodeByLang(lang.fields.name.value), pageAlpha2Code))) {
    if (isLang && a.fields?.tag?.value == siteIdentification?.fields?.identifying?.value) {
      return a?.fields?.name?.value;
    }
  }
  return 'Global';
};
export const isGlobalSite = () => {
  if (!canUseDOM()) return false;
  const lang = getLangFromUrl();
  return isNoW(lang) || /^en$/gi.test(lang);
};
export const getCurrentAlpha2Code = () => {
  if (isGlobalSite()) return null;
  const lang = getLangFromUrl();
  return getAlpha2CodeByLang(lang);
};

/**
 * 获取当前国家代码
 * 优先读取后端设置的 GSCountryCode Cookie，如果无效则回退到 URL 解析
 * @returns {string|null} 两位国家码 (如 'CN', 'JP')
 */
export const getCountryFromCookie = () => {
  if (!canUseDOM()) return null;

  const cookieCountry = getCookie('GSCountryCode');
  // Cookie 存在且为合法的两位字母国家码
  if (cookieCountry && /^[A-Za-z]{2}$/.test(cookieCountry)) {
    return cookieCountry.toUpperCase();
  }

  // 回退到原有的 URL 解析逻辑
  return getCurrentAlpha2Code();
};
export const getPageAlpha2Code = (page) => {
  // if (isGlobalSite()) return null;
  const { itemLanguage } = page;
  // return getAlpha2CodeByLang(itemLanguage);
  return itemLanguage;
};
export const getPageLanguage = (page) => {
  if (isGlobalSite()) return 'en';
  const { itemLanguage } = page;
  const [language] = itemLanguage.split('-');
  return language.toLowerCase();
};
export const getPageRegion = (page) => {
  const pageAlpha2Code = getPageAlpha2Code(page);
  const [areas] = getGlobalConfigs(page, 'areas');
  if (!areas?.length) return null;
  for (let area of areas) {
    for (let region of area.fields.regions) {
      const { alpha2Code, sfLang } = region.fields || {};
      if (eq(alpha2Code.value, pageAlpha2Code)) {
        return {
          name: region.name,
          displayName: region.displayName,
          alpha2Code: pageAlpha2Code,
          sfLang: sfLang?.value,
        };
      }
    }
  }
  return null;
};
export const getAlpha2CodeByLang = (lang) => {
  if (!isString(lang) || lang.indexOf('-') <= 0) return null;
  const [, code] = lang.split('-');
  return code.toUpperCase();
};
/* eslint-disable @typescript-eslint/no-unused-vars */
// 添加URL验证和清理函数
const _sanitizeUrlComponent = (component) => {
  if (!component) return '';

  // 移除或编码危险的协议
  const dangerousProtocols = [
    'javascript:',
    'data:',
    'vbscript:',
    'file:',
    'about:',
    'chrome:',
    'chrome-extension:',
  ];
  const lowerComponent = component.toLowerCase();

  for (const protocol of dangerousProtocols) {
    if (lowerComponent.startsWith(protocol)) {
      return '';
    }
  }

  // 移除HTML实体和脚本标签
  return component
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/data:/gi, '')
    .replace(/vbscript:/gi, '')
    .replace(/file:/gi, '')
    .replace(/about:/gi, '')
    .replace(/chrome:/gi, '')
    .replace(/chrome-extension:/gi, '');
};

/* eslint-disable @typescript-eslint/no-unused-vars */
// 安全的URL构建函数
const _buildSafeUrl = (_origin, _lang, _pathname, _search, _hash) => {
  try {
    // 使用URL构造函数进行安全验证
    const baseUrl = new URL(origin);

    // 验证origin必须是http或https
    if (!['http:', 'https:'].includes(baseUrl.protocol)) {
      throw new Error('Invalid protocol');
    }

    // 构建路径，确保安全
    let safePath = pathname === '/' ? '' : pathname;
    if (safePath && !safePath.startsWith('/')) {
      safePath = '/' + safePath;
    }

    // 构建完整的URL
    const url = new URL(`/${lang}${safePath}${search}${hash}`, baseUrl);

    // 最终验证
    if (url.origin !== baseUrl.origin) {
      throw new Error('Origin mismatch');
    }

    return url.href;
  } catch (error) {
    console.error('URL construction failed:', error);
    return null;
  }
};

export const checkLangOfUrl = () => {
  if (!canUseDOM()) return;
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const {
    origin: _origin,
    pathname: _pathname,
    search: _search,
    hash: _hash,
    hostname,
  } = window.location;
  const _reg = new RegExp(`^/(en|[A-Z]{2}|[a-z]{2})(/|$)`, 'gi');
  let _lang = 'en';

  if (hostname.indexOf('nl') > -1) _lang = 'nl';
  if (hostname.indexOf('pl') > -1) _lang = 'pl';

  // 注释掉自动重定向到带语言前缀的URL的逻辑
  // if (!reg.test(pathname)) {
  //   // 安全地清理和验证所有URL组件
  //   const sanitizedOrigin = sanitizeUrlComponent(origin);
  //   const sanitizedPathname = sanitizeUrlComponent(pathname);
  //   const sanitizedSearch = sanitizeUrlComponent(search);
  //   const sanitizedHash = sanitizeUrlComponent(hash);
  //
  //   // 验证清理后的组件
  //   if (!sanitizedOrigin || sanitizedOrigin !== origin) {
  //     console.warn('Invalid origin detected, using fallback');
  //     return;
  //   }
  //
  //   // 使用安全的URL构建函数
  //   const safeUrl = buildSafeUrl(sanitizedOrigin, lang, sanitizedPathname, sanitizedSearch, sanitizedHash);
  //
  //   if (safeUrl) {
  //     window.location.replace(safeUrl);
  //   } else {
  //     console.error('Failed to construct safe URL');
  //   }
  // }

  // const matches = pathname.match(/^\/(en|[a-z]{2}-[A-Z]{2})\/$/gi);
  // if (matches && matches.length > 0) {
  //   window.location.replace(`${origin}${pathname.substring(0, pathname.length - 1)}${search}${hash}`);
  // }
};
export const scrollToTop = () => {
  window.scrollTo(0, 0);
};
export const scrollElToTop = (el) => {
  el?.scrollIntoView();
};
export const xml2Js = (xml, config) => {
  const x2js = require('x2js');
  const x2jsOne = new x2js(config);
  return x2jsOne.xml2js(xml);
};
export const loadYoutubeApi = async () => {
  try {
    const loaded = await injectScript('https://www.youtube.com/iframe_api', !!window.YT);
    return [loaded ? window.YT : null, null];
  } catch (ex) {
    return [null, ex];
  }
};
export const getRandomId = () => `${randomString(10, 2)}${new Date().getTime()}`;
export const getCountries = (page) => {
  let _countries = [];
  const [areas] = getGlobalConfigs(page, 'areas');
  if (!areas?.length) return [];
  for (let area of areas) {
    for (let region of area.fields.regions) {
      if (!region.fields.languages) continue;
      const _country = {
        code: region.name,
        text: region.displayName,
        data: {
          languages: region.fields.languages.map((y) => ({
            code: y.fields.code.value,
            text: y.fields.text.value,
            link: y.fields.link.value,
          })),
        },
      };
      _countries.push(_country);
    }
  }
  _countries.sort((a, b) => (b.code > a.code ? -1 : 1));
  return _countries;
};
export const getAdaptiveTextField = (
  deviceState,
  desktopField,
  mobileField,
  tabletField,
  desktopLargeField
) => {
  return getAdaptiveValueField(
    deviceState,
    desktopField,
    mobileField,
    tabletField,
    desktopLargeField,
    (x) => x?.value
  );
};
export const getAdaptiveImageField = (
  deviceState,
  desktopField,
  mobileField,
  tabletField,
  desktopLargeField
) => {
  return getAdaptiveValueField(
    deviceState,
    desktopField,
    mobileField,
    tabletField,
    desktopLargeField,
    (x) => x?.value?.src
  );
};
export const getAdaptiveValueField = (
  deviceState,
  desktopField,
  mobileField,
  tabletField,
  desktopLargeField,
  valueFn
) => {
  switch (deviceState.deviceType) {
    case 'mobile':
      return ifEmptyField([mobileField, tabletField, desktopField, desktopLargeField], valueFn);
    case 'tablet':
      return ifEmptyField([tabletField, mobileField], valueFn);
    case 'tablet-landscape':
    case 'desktop':
    default:
      return ifEmptyField([desktopField, tabletField, mobileField], valueFn);
    case 'desktop-large':
      return ifEmptyField([desktopLargeField, desktopField, tabletField, mobileField], valueFn);
  }
};
export const getAdaptiveField = (
  deviceState,
  desktopField,
  mobileField,
  tabletField,
  desktopLargeField
) => {
  switch (deviceState.deviceType) {
    case 'mobile':
      return mobileField ?? desktopField;
    case 'tablet':
      return tabletField ?? mobileField ?? desktopField;
    case 'tablet-landscape':
    case 'desktop':
      return desktopField;
    case 'desktop-large':
      return desktopLargeField ?? desktopField;
    default:
      return desktopField;
  }
};
export const getAdpRatio = (deviceState, ratioItem) => {
  if (!ratioItem?.fields) return undefined;
  const dr = utils.gs.getRatioVal(utils.sp.getVal(ratioItem.fields.desktopRatio));
  const mr = utils.gs.getRatioVal(utils.sp.getVal(ratioItem.fields.mobileRatio));
  switch (deviceState.deviceType) {
    case 'mobile':
      return mr ?? dr;
    case 'tablet-landscape':
    case 'desktop':
    default:
      return dr;
  }
};
export const tryField = (fields, valueFn) => {
  if (!isFn(valueFn) || !isArray(fields)) return null;
  for (let field of fields) {
    if (!isNoE(valueFn(field))) return field;
  }
  return null;
};
export const tryValueField = (...fields) => {
  return tryField(fields, (x) => x?.value);
};

export const encodeData = (data) => {
  return lzString.compressToEncodedURIComponent(JSON.stringify(data));
};
export const decodeData = (data) => {
  if (isNoW(data)) return null;
  const str = lzString.decompressFromEncodedURIComponent(data);
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
};
export const loadGRecaptcha = async (key, lang) => {
  await injectScript(
    `https://www.google.com/recaptcha/enterprise.js?render=${key}&hl=${lang}`,
    () => window.grecaptcha
  );
  return window.grecaptcha;
};
export const initializeGeeTest = (
  language,
  captchaId,
  captchaProduct = 'bind',
  container = null,
  _options = null,
  onSuccess
) => {
  return new Promise((resolve) => {
    import('../lib/gt4').then(() => {
      const options = merge(
        {
          captchaId: captchaId,
          language,
          product: captchaProduct,
        },
        _options
      );
      global.initGeetest4(options, (captcha) => {
        if (captchaProduct !== 'bind') {
          captcha.appendTo(container);
        }
        captcha.onSuccess(function () {
          const result = captcha.getValidate();
          result.captcha_id = captchaId;
          onSuccess && onSuccess(result);
          captcha.reset();
        });
        resolve(captcha);
      });
    });
  });
};
// export const validateGeeTest = (captcha, captchaId) => {
//   return new Promise((resolve) => {
//     captcha.onSuccess(function() {
//       const result = captcha.getValidate();
//       result.captcha_id = captchaId;
//       resolve(result);
//       captcha.reset();
//     });
//     try {
//       captcha.showCaptcha();
//     } catch (ex) {
//       console.log(ex);
//     }
//   });
// };
export const getGeeTestCaptchaIds = (page) => {
  const [geeTestCaptchaIds] = getGlobalConfigs(page, 'geeTestCaptchaIds');
  if (isNoE(geeTestCaptchaIds.value)) return {};
  try {
    return querystring.parse(geeTestCaptchaIds.value);
  } catch {
    return {};
  }
};
export const getGeeTestCaptchaId = (page, name) => {
  const geeTestCaptchaIds = getGeeTestCaptchaIds(page);
  return geeTestCaptchaIds[name] ?? null;
};
export const isAppBrowser = () => {
  if (!canUseDOM()) return false;
  const AndroidCmd = global.android?.callNative;
  const iosCmd = global.webkit?.messageHandlers?.callNative;
  return !!(AndroidCmd || iosCmd);
};
export const isOnApp = ($route) => {
  return eq($route.query?.referrer, 'app') || eq($route.query?.channel, 'app') || isAppBrowser();
};
export const appVisible = ($route, fields) => {
  if (!fields?.hideOnApp?.value) return true;
  return !isOnApp($route);
};

export const formatCarPrice = (routeData, priceData) => {
  if (isNoE(routeData) || isNoE(priceData)) return routeData;
  const keys = Object.keys(routeData);
  for (let key of keys) {
    const item = routeData[key];
    const type = getType(item);
    if (type === '[object String]') {
      routeData[key] = formatString(item, priceData);
    } else if (type === '[object Object]') {
      routeData[key] = formatCarPrice(item, priceData);
    } else if (type === '[object Array]') {
      for (let subItem of item) {
        formatCarPrice(subItem, priceData);
      }
    }
  }
  return routeData;
};
export const getMarket = (alpha2Code) => {
  if (isNoE(alpha2Code)) return 'NL';
  const markets = {
    EU: ['NL', 'DE', 'FR', 'BE', 'NO', 'IT', 'BE', 'IT'],
    GB: ['GB'],
  };
  return markets.EU.includes(alpha2Code) ? 'EU' : markets.GB.includes(alpha2Code) ? 'GB' : 'ITIL';
};
export const getPageMarket = (page) => {
  const alpha2Code = getPageAlpha2Code(page);
  return getMarket(alpha2Code);
};
export const getCurrentMarket = () => {
  const alpha2Code = getCurrentAlpha2Code();
  return getMarket(alpha2Code);
};
export const loadAcsb = async (page) => {
  const [accessibeLib] = getGlobalConfigs(page, 'accessibeLib');
  const accessibeUrl = accessibeLib?.value;
  if (isNoE(accessibeUrl)) return;
  let locale = document.documentElement.lang;
  let localeparts = locale.split('-');
  let language = localeparts[0];
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'childList' &&
        mutation.addedNodes.length > 0 &&
        mutation.addedNodes[0].tagName === 'ACCESS-WIDGET-UI'
      ) {
        setTimeout(() => {
          let acsbWigets = document.querySelectorAll('access-widget-ui[data-acsb="widget"]');
          let acsbs = document.querySelectorAll('access-widget-ui[data-acsb=""]');
          if (acsbWigets.length > 0) {
            const acsb = acsbWigets[0];
            if (acsb) {
              const style = document.createElement('style');
              acsb.shadowRoot.appendChild(style);
              style.sheet.insertRule(
                '.language-selector .flag, .language-selection__content .language__flag {display:none !important;}'
              );
              style.sheet.insertRule(
                '.language-selection__content button.language {justify-content: flex-start;}'
              );
              style.sheet.insertRule(
                '.language-selection__content button.language:nth-of-type(8) {display: none !important;}'
              );
              style.sheet.insertRule(
                '.language-selection__content button.language:nth-of-type(11) {display: none !important;}'
              );
            }
          }
          if (acsbs.length > 0) {
            const acsb = acsbs[0];
            if (acsb) {
              const style = document.createElement('style');
              acsb.shadowRoot.appendChild(style);
              style.sheet.insertRule('::part(acsb-trigger) {z-index: 10 !important;}');
            }
          }
        }, 100);
      }
    });
  });
  observer.observe(document.body, {
    childList: true,
  });
  await injectScript(accessibeUrl, () => !!global.acsbJS);
  global.acsbJS?.init({
    statementLink: '',
    feedbackLink: '',
    footerHtml: '',
    hideMobile: false,
    hideTrigger: false,
    language: language,
    position: 'left',
    leadColor: '#000',
    triggerColor: '#fff200',
    triggerRadius: '0',
    triggerPositionX: 'left',
    triggerPositionY: 'bottom',
    triggerIcon: 'default',
    triggerSize: 'small',
    triggerOffsetX: 22,
    triggerOffsetY: 92,
    mobile: {
      triggerSize: 'small',
      triggerPositionX: 'left',
      triggerPositionY: 'bottom',
      triggerOffsetX: 16,
      triggerOffsetY: 16,
      triggerRadius: '0',
    },
  });
};
export const findComponent = (page, componentName, placeholder = 'jss-main') => {
  const placeholders = Object.keys(page.placeholders);
  if (isNoE(placeholder)) {
    for (let ph of placeholders) {
      if (!page.placeholders[ph]?.length) continue;
      const cm = page.placeholders[ph].find((x) => eq(x.componentName, componentName));
      if (cm) {
        return cm;
      }
    }
    return null;
  }
  if (!page.placeholders[placeholder]) return null;
  return page.placeholders[placeholder].find((x) => eq(x.componentName, componentName));
};
export const formatModel = (model, caseType = 'uppercase') => {
  let result = model?.replace(/(\s+(base|i4))/gi, '');
  const caseFn = {
    uppercase(str) {
      return str?.toUpperCase();
    },
    capitalize(str) {
      return toCapitalize(str);
    },
  };
  if (!caseFn[caseType]) return result;
  return caseFn[caseType](result);
};
export const formDdl = (field) => {
  if (!field?.fields) return null;
  const { title, label, placeholder, description, options, clearable, clearText, hideSelected } =
    field.fields;
  return {
    title: title.value,
    label: label.value,
    placeholder: placeholder.value,
    description: description.value,
    clearable: clearable.value,
    clearText: clearText?.value,
    hideSelected: hideSelected.value,
    options: options.map((x) => ({
      code: x.fields.code.value,
      text: x.fields.text.value,
    })),
  };
};
export const destroySwiper = (swiper, ...options) => {
  try {
    swiper?.destroy(...options);
  } catch (ex) {
    console.log(ex);
  }
};

/**
 * 加载 ScrollMagic 库
 * @returns {Object|null} ScrollMagic 对象或 null
 */
export const loadScrollMagic = () => {
  if (typeof window !== 'undefined' && window.ScrollMagic) {
    return window.ScrollMagic;
  }
  return null;
};
